import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Eye, Archive, Upload, Globe, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, asset } from "../../lib/api";
import { SCHEMAS } from "./schemas";
import { GAMES } from "../lesson/Games";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

const UploadField = ({ value, onChange, kind, testid }) => {
  const [busy, setBusy] = useState(false);
  const up = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/admin/upload", fd);
      onChange(data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="URL or upload" data-testid={testid} />
        <label className="shrink-0 h-10 px-3 rounded-md border flex items-center gap-1 text-sm cursor-pointer hover:bg-amber-50">
          <Upload size={14} /> {busy ? "…" : "Upload"}
          <input type="file" className="hidden" accept={kind === "image" ? "image/*" : "audio/*"} onChange={up} data-testid={`${testid}-file`} />
        </label>
      </div>
      {value && kind === "image" && <img src={asset(value)} alt="" className="h-20 rounded-lg border" />}
      {value && kind === "audio" && <audio src={asset(value)} controls className="h-8 w-full" />}
    </div>
  );
};

const Field = ({ name, type, value, onChange, stories, jsonText, setJsonText }) => {
  const tid = `field-${name}`;
  if (type === "readonly") return <Input data-testid={tid} value={value ?? ""} disabled />;
  if (Array.isArray(type))
    return <select data-testid={tid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full h-10 rounded-md border px-3 bg-white">{type.map((o) => <option key={o}>{o}</option>)}</select>;
  if (type === "story")
    return <select data-testid={tid} value={value ?? ""} onChange={(e) => onChange(e.target.value || null)} className="w-full h-10 rounded-md border px-3 bg-white"><option value="">—</option>{stories.map((s) => <option key={s.story_id} value={s.story_id}>{s.story_number}. {s.story_title}</option>)}</select>;
  if (type === "number") return <Input data-testid={tid} type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} />;
  if (type === "textarea") return <Textarea data-testid={tid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={3} />;
  if (type === "bool") return <Switch data-testid={tid} checked={!!value} onCheckedChange={onChange} />;
  if (type === "image" || type === "audio") return <UploadField value={value} onChange={onChange} kind={type} testid={tid} />;
  if (type === "json") return <Textarea data-testid={tid} className="font-mono text-xs" rows={6} value={jsonText[name] ?? JSON.stringify(value ?? null, null, 2)} onChange={(e) => setJsonText({ ...jsonText, [name]: e.target.value })} />;
  return <Input data-testid={tid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
};

const ActivityPreview = ({ activity, onClose }) => {
  const G = activity && GAMES[activity.activity_type];
  return (
    <Dialog open={!!activity} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl paper-bg" data-testid="activity-preview-dialog">
        <DialogHeader><DialogTitle className="font-en">Preview · {activity?.activity_type}</DialogTitle></DialogHeader>
        {G && <div className="py-6"><G key={activity.activity_id} activity={activity} record={() => {}} onDone={(ok) => toast(ok ? "Correct on first try" : "Finished")} /></div>}
      </DialogContent>
    </Dialog>
  );
};

export const CollectionEditor = ({ name, stories, onChanged }) => {
  const schema = SCHEMAS[name];
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [edit, setEdit] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [jsonText, setJsonText] = useState({});
  const [preview, setPreview] = useState(null);

  const load = () => api.get(`/admin/c/${name}`, { params: filter ? { story_id: filter } : {} }).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, [name, filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = useMemo(() => rows.filter((r) => !q || JSON.stringify(r).includes(q)), [rows, q]);
  const open = (r) => { setEdit(r ? { ...r } : { status: "draft", ...(filter && schema.filter ? { [schema.filter]: filter } : {}) }); setIsNew(!r); setJsonText({}); };

  const save = async () => {
    const body = { ...edit };
    try {
      for (const [k, v] of Object.entries(jsonText)) body[k] = JSON.parse(v);
    } catch {
      return toast.error("Invalid JSON in one of the fields");
    }
    try {
      if (isNew) await api.post(`/admin/c/${name}`, body);
      else await api.put(`/admin/c/${name}/${body[schema.key]}`, body);
      toast.success("Saved");
      setEdit(null);
      load();
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const patch = async (r, p) => { await api.put(`/admin/c/${name}/${r[schema.key]}`, p); load(); onChanged && onChanged(); };
  const archive = async (r) => { if (window.confirm("Archive this item? It will be hidden from learners.")) { await api.delete(`/admin/c/${name}/${r[schema.key]}`); load(); } };

  return (
    <div className="space-y-4" data-testid={`admin-collection-${name}`}>
      <div className="flex flex-wrap gap-3 items-center">
        <Input data-testid="admin-search-input" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs bg-white" />
        {schema.filter && (
          <select data-testid="admin-story-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="h-10 rounded-md border px-3 bg-white">
            <option value="">All stories</option>
            {stories.map((s) => <option key={s.story_id} value={s.story_id}>{s.story_number}. {s.story_title}</option>)}
          </select>
        )}
        <div className="flex-1" />
        <span className="text-sm text-stone-500">{shown.length} items</span>
        <button data-testid="admin-create-btn" onClick={() => open(null)} className="h-10 px-4 rounded-full bg-[var(--vermilion)] text-white text-sm font-semibold flex items-center gap-2"><Plus size={16} /> New</button>
      </div>
      <div className="scroll-card !rounded-2xl overflow-hidden">
        <div className="max-h-[65vh] overflow-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-amber-50 z-10"><TableRow>{schema.columns.map((c) => <TableHead key={c}>{c}</TableHead>)}<TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {shown.map((r) => (
                <TableRow key={r[schema.key]} data-testid={`admin-row-${r[schema.key]}`}>
                  {schema.columns.map((c) => (
                    <TableCell key={c} className={`max-w-[360px] truncate ${["story_title", "symbol", "traditional_character"].includes(c) ? "font-serif-tc text-lg font-bold" : ""}`}>
                      {c === "status" ? <span className={`px-2 py-0.5 rounded-full text-xs ${r.status === "published" ? "bg-green-100 text-green-700" : "bg-stone-100 text-stone-600"}`}>{r.status}</span> : String(r[c] ?? "")}
                    </TableCell>
                  ))}
                  <TableCell className="text-right whitespace-nowrap">
                    {name === "stories" && <button title="Preview" data-testid={`preview-${r.story_id}`} onClick={() => window.open(`/lesson/${r.story_id}?preview=1`, "_self")} className="p-2 hover:bg-amber-50 rounded"><Eye size={16} /></button>}
                    {name === "activities" && <button title="Preview" data-testid={`preview-${r.activity_id}`} onClick={() => setPreview(r)} className="p-2 hover:bg-amber-50 rounded"><Eye size={16} /></button>}
                    {"status" in r && (
                      <button title={r.status === "published" ? "Unpublish" : "Publish"} data-testid={`toggle-publish-${r[schema.key]}`} onClick={() => patch(r, { status: r.status === "published" ? "draft" : "published" })} className="p-2 hover:bg-amber-50 rounded">
                        {r.status === "published" ? <EyeOff size={16} /> : <Globe size={16} className="text-green-700" />}
                      </button>
                    )}
                    <button title="Edit" data-testid={`edit-${r[schema.key]}`} onClick={() => open(r)} className="p-2 hover:bg-amber-50 rounded"><Pencil size={16} /></button>
                    <button title="Archive" data-testid={`archive-${r[schema.key]}`} onClick={() => archive(r)} className="p-2 hover:bg-red-50 rounded text-red-600"><Archive size={16} /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
      <Sheet open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-auto font-en" data-testid="admin-edit-sheet">
          <SheetHeader><SheetTitle>{isNew ? "New" : "Edit"} {schema.singular}</SheetTitle></SheetHeader>
          {edit && (
            <div className="space-y-4 mt-6 pb-10">
              {schema.fields.map(([f, t]) => (
                <div key={f} className="space-y-1">
                  <Label className="text-xs text-stone-500">{f}</Label>
                  <Field name={f} type={f === schema.key && !isNew ? "readonly" : t} value={edit[f]} stories={stories}
                    onChange={(v) => setEdit({ ...edit, [f]: v })} jsonText={jsonText} setJsonText={setJsonText} />
                </div>
              ))}
              <button data-testid="admin-save-btn" onClick={save} className="w-full h-11 rounded-full bg-[var(--vermilion)] text-white font-semibold">Save</button>
            </div>
          )}
        </SheetContent>
      </Sheet>
      <ActivityPreview activity={preview} onClose={() => setPreview(null)} />
    </div>
  );
};
